import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { CheckCircle2, Clock, CreditCard, ShieldCheck, ArrowLeft, ArrowRight } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../hooks/useAuth';
import { PRICING_CONFIG } from '../../config/pricingConfig';
import {
    isLegalDocsPublished,
    isLegalAcceptanceRequired,
    getPublishedTermsVersion,
    getPublishedPrivacyVersion,
} from '../../config/legalConfig';
import { usePageSEO } from '../../utils/seo';

function extractInitialInviteParams(): { inviteToken: string; inviteEmail: string } {
    if (typeof window === 'undefined') {
        return { inviteToken: '', inviteEmail: '' };
    }
    const hashRaw = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
    const hashParams = new URLSearchParams(hashRaw);
    const searchParams = new URLSearchParams(window.location.search);
    const inviteToken = (hashParams.get('invite') || searchParams.get('invite') || '').trim();
    const inviteEmail = (hashParams.get('email') || searchParams.get('email') || '').trim();
    return { inviteToken, inviteEmail };
}

export function Register() {
    const [, setLocation] = useLocation();
    const { login } = useAuthStore();
    const [{ inviteToken, inviteEmail: initialInviteEmail }] = useState(extractInitialInviteParams);
    const [formData, setFormData] = useState({
        email: initialInviteEmail,
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'warehouse_manager' as const,
    });
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const legalAcceptanceRequired = isLegalAcceptanceRequired();

    // Immediately scrub any invite token from browser-visible URL bar & history on mount
    useEffect(() => {
        if (
            typeof window !== 'undefined' &&
            (window.location.search.includes('invite=') || window.location.hash.includes('invite='))
        ) {
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, []);

    usePageSEO({
        title: 'Start Your Free 30-Day Trial — BayWhiz Warehouse Dock Scheduling',
        description:
            'Create your BayWhiz warehouse manager account and start a 30-day full-access dock scheduling trial. No credit card required.',
        canonicalPath: '/register',
    });

    // Client-side field validation
    const validateFields = (): Record<string, string> => {
        const errors: Record<string, string> = {};
        if (!formData.firstName.trim()) errors.firstName = 'First name is required';
        if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            errors.email = 'Invalid email format';
        }
        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        }
        if (formData.phone.trim()) {
            const phoneDigits = formData.phone.replace(/\D/g, '').length;
            if (phoneDigits < 10 || phoneDigits > 11) {
                errors.phone = 'Phone must be 10 digits (or 11 with country code)';
            }
        }
        return errors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        // Client-side validation
        const clientErrors = validateFields();
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            setError(Object.values(clientErrors)[0]);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setFieldErrors({ confirmPassword: 'Passwords do not match' });
            setLoading(false);
            return;
        }

        if (legalAcceptanceRequired && !acceptedTerms) {
            const msg = 'You must accept the Terms of Service and acknowledge the Privacy Policy to create an account.';
            setError(msg);
            setFieldErrors({ acceptedTerms: msg });
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await api.register({
                ...formData,
                phone: formData.phone.trim() || undefined,
                ...(inviteToken ? { inviteToken } : {}),
                ...(legalAcceptanceRequired
                    ? {
                          legalAcceptance: {
                              acceptedTerms: true,
                              acknowledgedPrivacy: true,
                              termsVersion: getPublishedTermsVersion(),
                              privacyVersion: getPublishedPrivacyVersion(),
                          },
                      }
                    : {}),
            }) as { user: any; token: string };
            login(response.user, response.token);
            setLocation(response.user?.warehouseId ? '/dashboard' : '/facilities?from=onboarding');
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message);
                // Apply server field errors if available
                if (err.details?.fieldErrors) {
                    setFieldErrors(err.details.fieldErrors);
                }
            } else {
                setError('Registration failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const fieldClass = (field: string) =>
        `w-full bg-white border ${fieldErrors[field] ? 'border-red-500' : 'border-slate-300'} rounded-lg px-3.5 py-2.5 text-slate-900 focus:outline-none focus:ring-2 ${fieldErrors[field] ? 'focus:ring-red-500/50' : 'focus:ring-teal-500/40 focus:border-teal-600'}`;

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center px-4 sm:px-6 py-10">
            <div className="max-w-5xl w-full mx-auto">
                {/* Back to homepage link */}
                <div className="mb-6 flex items-center justify-between">
                    <Link
                        href="/"
                        className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to BayWhiz.com
                    </Link>
                    <Link
                        href="/pricing"
                        className="text-xs font-semibold text-teal-700 hover:text-teal-800"
                    >
                        View Pricing &amp; Trial Details &rarr;
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Registration Form Card */}
                    <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-lg">
                        {!inviteToken ? (
                            <div data-testid="warehouse-invite-required-notice" className="space-y-5 py-2">
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-3">
                                        <Clock size={13} />
                                        {PRICING_CONFIG.trialDays}-Day Full-Access Warehouse Trial
                                    </div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 tracking-tight">
                                        Warehouse Trial Access Requires Approval
                                    </h1>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Warehouse registration currently requires a founder-approved, single-use invitation bound to your work email. You do not need an account or credit card to request access.
                                    </p>
                                </div>

                                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs sm:text-sm text-slate-700 space-y-2">
                                    <p className="font-semibold text-slate-900">
                                        How to get your warehouse trial invitation:
                                    </p>
                                    <p className="leading-relaxed text-slate-600">
                                        Submit a quick trial request with your company name, work email, and facility location. Once approved, we will send your single-use registration link so you can start your 30-day full Operations trial.
                                    </p>
                                </div>

                                <div className="pt-1">
                                    <Link
                                        href="/request-trial"
                                        data-testid="register-request-trial-link"
                                        className="h-12 w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold text-sm transition shadow-sm inline-flex items-center justify-center gap-2"
                                    >
                                        Request Trial Access
                                        <ArrowRight size={16} />
                                    </Link>
                                </div>

                                <div className="text-center text-sm text-slate-600 mt-4 pt-4 border-t border-slate-100 space-y-1.5">
                                    <p>
                                        Already have an approved invitation? Open the link from your invitation email.
                                    </p>
                                    <p>
                                        Already have an account?{' '}
                                        <Link href="/login" className="text-teal-700 font-semibold hover:underline">
                                            Sign in here
                                        </Link>
                                    </p>
                                    <p>
                                        Are you a freight carrier?{' '}
                                        <Link href="/carrier-signup" className="text-teal-700 font-semibold hover:underline">
                                            Free carrier sign up &rarr;
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="mb-6">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-3">
                                        <Clock size={13} />
                                        {PRICING_CONFIG.trialDays}-Day Full-Access Warehouse Trial
                                    </div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1.5 tracking-tight">
                                        Create your warehouse manager account
                                    </h1>
                                    <p className="text-sm text-slate-600">
                                        You&apos;ll configure your warehouse facility and dock doors in the next step. No credit card required.
                                    </p>
                                    <div
                                        data-testid="warehouse-invite-active-banner"
                                        className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3.5 py-2.5 text-xs text-emerald-800"
                                    >
                                        <strong>Approved Trial Invitation Verified:</strong> This single-use invitation is bound to{' '}
                                        <span className="font-mono font-semibold">{formData.email || 'your approved email'}</span>.
                                    </div>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-6 text-center space-y-1.5">
                                        <div>{error}</div>
                                        <div>
                                            <Link
                                                href="/request-trial"
                                                className="text-xs font-semibold text-red-800 underline hover:text-red-900"
                                            >
                                                Need a new trial invitation? Request Trial Access &rarr;
                                            </Link>
                                        </div>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                                        First Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="firstName"
                                        type="text"
                                        className={fieldClass('firstName')}
                                        value={formData.firstName}
                                        onChange={(e) => {
                                            setFormData({ ...formData, firstName: e.target.value });
                                            setFieldErrors((prev) => {
                                                const { firstName, ...rest } = prev;
                                                return rest;
                                            });
                                        }}
                                        placeholder="Jordan"
                                        required
                                    />
                                    {fieldErrors.firstName && (
                                        <p className="text-xs text-red-500 mt-0.5">{fieldErrors.firstName}</p>
                                    )}
                                </div>

                                <div className="space-y-1">
                                    <label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                                        Last Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="lastName"
                                        type="text"
                                        className={fieldClass('lastName')}
                                        value={formData.lastName}
                                        onChange={(e) => {
                                            setFormData({ ...formData, lastName: e.target.value });
                                            setFieldErrors((prev) => {
                                                const { lastName, ...rest } = prev;
                                                return rest;
                                            });
                                        }}
                                        placeholder="Mercer"
                                        required
                                    />
                                    {fieldErrors.lastName && (
                                        <p className="text-xs text-red-500 mt-0.5">{fieldErrors.lastName}</p>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label htmlFor="email" className="text-sm font-medium text-slate-700">
                                    Work Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    className={fieldClass('email')}
                                    value={formData.email}
                                    readOnly={Boolean(inviteToken && initialInviteEmail)}
                                    onChange={(e) => {
                                        setFormData({ ...formData, email: e.target.value });
                                        setFieldErrors((prev) => {
                                            const { email, ...rest } = prev;
                                            return rest;
                                        });
                                    }}
                                    placeholder="you@warehouse.com"
                                    required
                                />
                                {fieldErrors.email && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.email}</p>}
                            </div>

                            <div className="space-y-1">
                                <label htmlFor="phone" className="text-sm font-medium text-slate-700">
                                    Phone <span className="text-slate-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    id="phone"
                                    type="tel"
                                    className={fieldClass('phone')}
                                    value={formData.phone}
                                    onChange={(e) => {
                                        setFormData({ ...formData, phone: e.target.value });
                                        setFieldErrors((prev) => {
                                            const { phone, ...rest } = prev;
                                            return rest;
                                        });
                                    }}
                                    placeholder="(555) 123-4567"
                                />
                                {fieldErrors.phone && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.phone}</p>}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label htmlFor="password" className="text-sm font-medium text-slate-700">
                                        Password <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="password"
                                        type="password"
                                        className={fieldClass('password')}
                                        value={formData.password}
                                        onChange={(e) => {
                                            setFormData({ ...formData, password: e.target.value });
                                            setFieldErrors((prev) => {
                                                const { password, ...rest } = prev;
                                                return rest;
                                            });
                                        }}
                                        placeholder="At least 8 characters"
                                        required
                                    />
                                    {fieldErrors.password && (
                                        <p className="text-xs text-red-500 mt-0.5">{fieldErrors.password}</p>
                                    )}
                                </div>

                                <div className="space-y-1">
                                    <label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                                        Confirm Password <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="confirmPassword"
                                        type="password"
                                        className={fieldClass('confirmPassword')}
                                        value={formData.confirmPassword}
                                        onChange={(e) => {
                                            setFormData({ ...formData, confirmPassword: e.target.value });
                                            setFieldErrors((prev) => {
                                                const { confirmPassword, ...rest } = prev;
                                                return rest;
                                            });
                                        }}
                                        placeholder="Repeat password"
                                        required
                                    />
                                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                        <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                                    )}
                                </div>
                            </div>

                            {/* Role is automatically set to warehouse_manager — no user choice needed */}

                            {legalAcceptanceRequired && (
                                <div
                                    data-testid="warehouse-legal-acceptance-section"
                                    className={`rounded-xl border p-3.5 ${
                                        fieldErrors.acceptedTerms
                                            ? 'border-red-300 bg-red-50/50'
                                            : 'border-slate-200 bg-slate-50'
                                    }`}
                                >
                                    <label
                                        htmlFor="warehouse-accept-legal"
                                        className="flex items-start gap-3 text-xs sm:text-sm text-slate-700 cursor-pointer"
                                    >
                                        <input
                                            id="warehouse-accept-legal"
                                            data-testid="warehouse-accept-legal-checkbox"
                                            type="checkbox"
                                            checked={acceptedTerms}
                                            onChange={(e) => {
                                                setAcceptedTerms(e.target.checked);
                                                setFieldErrors((prev) => {
                                                    const { acceptedTerms: _omit, ...rest } = prev;
                                                    return rest;
                                                });
                                            }}
                                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                        />
                                        <span className="leading-relaxed">
                                            I confirm that I am authorized to act on behalf of the warehouse business I am registering and agree to the{' '}
                                            <a
                                                href="/terms"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="warehouse-terms-link"
                                                className="font-semibold text-teal-700 hover:underline"
                                            >
                                                BayWhiz Terms of Service
                                            </a>{' '}
                                            (version <span data-testid="warehouse-terms-version">{getPublishedTermsVersion()}</span>). I acknowledge that I have read the{' '}
                                            <a
                                                href="/privacy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="warehouse-privacy-link"
                                                className="font-semibold text-teal-700 hover:underline"
                                            >
                                                BayWhiz Privacy Policy
                                            </a>{' '}
                                            (version <span data-testid="warehouse-privacy-version">{getPublishedPrivacyVersion()}</span>), which explains how BayWhiz handles personal information.
                                        </span>
                                    </label>
                                    <p
                                        data-testid="warehouse-legal-helper-text"
                                        className="text-xs text-slate-500 mt-2 pl-7 leading-relaxed"
                                    >
                                        This is a free 30-day trial. No payment information is collected, and nothing is charged automatically when the trial ends.
                                    </p>
                                    {fieldErrors.acceptedTerms && (
                                        <p className="text-xs text-red-600 mt-1.5 pl-7">{fieldErrors.acceptedTerms}</p>
                                    )}
                                </div>
                            )}

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    className="h-12 w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition disabled:opacity-60 cursor-pointer shadow-sm"
                                    disabled={
                                        loading ||
                                        (formData.confirmPassword !== '' && formData.password !== formData.confirmPassword)
                                    }
                                >
                                    {loading ? 'Creating Account...' : 'Start Free 30-Day Trial'}
                                </button>
                            </div>

                            <div className="text-center text-sm text-slate-600 mt-4 pt-3 border-t border-slate-100 space-y-1.5">
                                <p>
                                    Already have an account?{' '}
                                    <Link href="/login" className="text-teal-700 font-semibold hover:underline">
                                        Sign in here
                                    </Link>
                                </p>
                                <p>
                                    Are you a freight carrier?{' '}
                                    <Link href="/carrier-signup" className="text-teal-700 font-semibold hover:underline">
                                        Free carrier sign up &rarr;
                                    </Link>
                                </p>
                            </div>
                        </form>
                            </>
                        )}
                    </div>

                    {/* Concise Trial Explanation Panel */}
                    <aside
                        aria-label="30-day free trial details"
                        className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 shadow-sm space-y-6"
                    >
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 mb-1.5">
                                How your 30-day free trial works
                            </h2>
                            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                                Evaluate BayWhiz with your receiving team before making any billing commitment.
                            </p>
                        </div>

                        <ol className="space-y-4 text-sm">
                            <li className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                    1
                                </span>
                                <div>
                                    <p className="font-semibold text-slate-900">Today: Instant full-access setup</p>
                                    <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                                        Create your account, set up your facility timezone and dock doors, and get your public carrier booking link immediately.
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                    2
                                </span>
                                <div>
                                    <p className="font-semibold text-slate-900">Days 1&ndash;30: Run live dock operations</p>
                                    <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                                        Import or create POs, approve carrier booking requests, assign dock doors, and track check-ins and dwell time.
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                    3
                                </span>
                                <div>
                                    <p className="font-semibold text-slate-900">After Day 30: Subscribe only if you continue</p>
                                    <p className="text-xs text-slate-600 leading-relaxed mt-0.5">
                                        Because no credit card is collected today, you will never be auto-charged. If no paid subscription is activated after day 30, operational access pauses safely&mdash;contact {PRICING_CONFIG.supportEmail} to activate Starter (${PRICING_CONFIG.plans.starter.priceMonthlyUsd} USD/mo) or Operations (${PRICING_CONFIG.plans.operations.priceMonthlyUsd} USD/mo) per facility.
                                    </p>
                                </div>
                            </li>
                        </ol>

                        <div className="pt-4 border-t border-slate-200 space-y-2.5 text-xs text-slate-600">
                            <div className="flex items-center gap-2">
                                <CreditCard size={15} className="text-teal-600 shrink-0" />
                                <span>No credit card required to start</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={15} className="text-teal-600 shrink-0" />
                                <span>All dock scheduling, carrier portal &amp; dwell features included</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={15} className="text-teal-600 shrink-0" />
                                <span>Your facility data is preserved if you subscribe after the trial</span>
                            </div>
                        </div>
                    </aside>
                </div>

                {isLegalDocsPublished() && (
                    <footer
                        data-testid="register-legal-footer"
                        className="mt-8 pt-4 border-t border-slate-200 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-slate-500"
                    >
                        <Link href="/terms" className="hover:text-teal-700 hover:underline font-medium">
                            Terms of Service
                        </Link>
                        <span aria-hidden="true">·</span>
                        <Link href="/privacy" className="hover:text-teal-700 hover:underline font-medium">
                            Privacy Policy
                        </Link>
                        <span aria-hidden="true">·</span>
                        <a href="mailto:support@baywhiz.com" className="hover:text-teal-700 hover:underline">
                            support@baywhiz.com
                        </a>
                    </footer>
                )}
            </div>
        </div>
    );
}
